/**
 * Axios Store
 *
 * Tracks pending HTTP requests for loading state indicators.
 * Simple store with just an array of request IDs.
 *
 * **Architecture**: Store owner creates curated custom hooks for consumers
 * - Store contains state + actions
 * - Custom hooks provide granular, optimized access to state
 * - Actions hook provides mutation functions (never causes re-renders)
 * - Consumers import only custom hooks, never raw store access
 */

import { type GetStoreState, type SetStoreState, createStore, createStoreInstance } from '@/stores/createStore';

// ========================================
// TYPES
// ========================================

export interface AxiosState {
  pendingRequests: string[];
}

const initialState: AxiosState = {
  pendingRequests: [],
};

// ========================================
// STORE
// ========================================

const axiosActionsFactory = (set: SetStoreState<AxiosState>, _get: GetStoreState<AxiosState>) => ({
  /**
   * Add a request ID to the pending requests array
   */
  addRequest: (requestId?: string) => {
    if (requestId) {
      set((draft: AxiosState) => {
        draft.pendingRequests.push(requestId);
      });
    }
  },

  /**
   * Remove a request ID from the pending requests array
   */
  removeRequest: (requestId?: string) => {
    if (requestId) {
      set((draft: AxiosState) => {
        draft.pendingRequests = draft.pendingRequests.filter((id) => id !== requestId);
      });
    }
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(() => initialState);
  },
});

const [AxiosStoreProvider, useStore, useSelector] = createStore('Axios', initialState, axiosActionsFactory);

/**
 * Create an Axios store instance outside of React (for legacy singleton pattern)
 *
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 *
 * This allows creating a singleton store instance at module load time that can be
 * imported by legacy code that cannot use React hooks (e.g., axios interceptors).
 *
 * **Migration path**: Once all legacy code is migrated to use hooks:
 * 1. Update all imports of `sqAxiosStore` to use hooks instead
 * 2. Delete this export
 * 3. Delete `sqAxiosStore` from core.stores.ts
 * 4. Remove `store` prop usage from AxiosStoreProvider
 *
 * @example
 * ```tsx
 * // Legacy usage (to be migrated)
 * import { sqAxiosStore } from '@/core/core.stores';
 * sqAxiosStore.actions.addRequest('request-id');
 *
 * // Modern usage (migrate to this)
 * import { useAxiosActions } from '@/requests/axios.store';
 * const { addRequest } = useAxiosActions();
 * ```
 */
export function legacyCreateAxiosStore() {
  return createStoreInstance(initialState, axiosActionsFactory);
}

// ========================================
// EXPORTS: Provider
// ========================================

export { AxiosStoreProvider };

// ========================================
// EXPORTS: State Hooks (Fine-Grained Selectors)
// ========================================

/**
 * Get actions (NEVER causes re-renders)
 */
export const useAxiosActions = () => useStore().actions;

/**
 * Get array of pending request IDs
 */
export const usePendingRequests = () => useSelector((state) => state.pendingRequests);

/**
 * Get count of pending requests
 */
export const usePendingRequestsCount = () => useSelector((state) => state.pendingRequests.length);

/**
 * Check if any requests are pending
 */
export const useHasPendingRequests = () => useSelector((state) => state.pendingRequests.length > 0);
